import { Injectable, NgZone } from '@angular/core';
import { App } from '@capacitor/app';
import { Subscription, debounceTime, distinctUntilChanged, fromEvent } from 'rxjs';



@Injectable()
export class InactivityService {

  private inactivityTimeout: any;
  private readonly timeoutDuration = 1800000; // 30 minutes of inactivity in milliseconds
  // private readonly timeoutDuration = 60000; // 1 minute of inactivity in milliseconds
  private readonly debounceDuration = 300; //  300 milliseconds debounce
  private subscription: Subscription = new Subscription();

  callback!: Function;

  constructor(private ngZone: NgZone) {
    this.initializeInactivityDetection();

    // listen for app state changes to handle background/foreground states
    App.addListener('appStateChange', ({ isActive }) => {
      // console.log('App: appStateChange: isActive:', isActive);
      // console.log('App: isActive:', isActive);

      this.resetInactivityTimer('appStateChange');
    });
  }

  ngOnDestroy(): void {
    // Clean up subscriptions to prevent memory leaks
    this.subscription.unsubscribe();
  }

  // set up the listeners for detecting user interactions
  private initializeInactivityDetection(): void {
    // console.log('App: initializeInactivityDetection');

    this.ngZone.runOutsideAngular(() => {

      // fired when the user touches the screen
      const touchstart$ = fromEvent(document, 'touchstart')
      .pipe(
        debounceTime(this.debounceDuration), // apply debounce to the events
        distinctUntilChanged() // ensure only distinct values pass through
      );

      // triggered when the user stops touching the screen
      const touchend$ = fromEvent(document, 'touchend')
      .pipe(
        debounceTime(this.debounceDuration), // apply debounce to the events
        distinctUntilChanged() // ensure only distinct values pass through
      );

      // fired when the user moves their finger across the screen
      const touchmove$ = fromEvent(document, 'touchmove')
      .pipe(
        debounceTime(this.debounceDuration), // apply debounce to the events
        distinctUntilChanged() // ensure only distinct values pass through
      );

      // fired when the user scrolls the content within a scrollable element
      const scroll$ = fromEvent(document, 'scroll')
      .pipe(
        debounceTime(this.debounceDuration), // apply debounce to the events
        distinctUntilChanged() // ensure only distinct values pass through
      );

      // user presses or releases a key on the on-screen keyboar
      const keydown$ = fromEvent(document, 'keydown')
      .pipe(
        debounceTime(this.debounceDuration), // apply debounce to the events
        distinctUntilChanged() // ensure only distinct values pass through
      );

      // fired when the app's tab or window becomes hidden or visible
      const visibilitychange$ = fromEvent(document, 'visibilitychange')
      .pipe(
        debounceTime(this.debounceDuration), // apply debounce to the events
        distinctUntilChanged() // ensure only distinct values pass through
      );

      this.subscription.add(
        touchstart$.subscribe(() => this.resetInactivityTimer('touchstart'))
      );
      this.subscription.add(
        touchend$.subscribe(() => this.resetInactivityTimer('touchend'))
      );
      this.subscription.add(
        touchmove$.subscribe(() => this.resetInactivityTimer('touchmove'))
      );
      this.subscription.add(
        scroll$.subscribe(() => this.resetInactivityTimer('scroll'))
      );
      this.subscription.add(
        keydown$.subscribe(() => this.resetInactivityTimer('keydown'))
      );
      this.subscription.add(
        visibilitychange$.subscribe(() => this.resetInactivityTimer('visibilitychange'))
      );

    });

    this.resetInactivityTimer('initializeInactivityDetection func'); // initialize the timer
  }

  resetInactivityTimer(source: string): void {
    // console.log('App resetInactivityTimer:', source);
    this.clearInactivityTimer();

    this.ngZone.runOutsideAngular(() => {
      this.inactivityTimeout = setTimeout(() => this.handleInactivity(), this.timeoutDuration);
    });
  }

  private clearInactivityTimer(): void {
    // console.log('App clearInactivityTimer');
    clearTimeout(this.inactivityTimeout);
  }


  private handleInactivity(): void {
    console.log('User is inactive.');
    this.callback();
  }

}
